import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

function AdminSettings() {
    const [settings, setSettings] = useState({});
    const [dbConnections, setDbConnections] = useState([]);
    const [chatSettings, setChatSettings] = useState({ chat_enabled: true, notification_sound: true });
    const [requestModes, setRequestModes] = useState({
        slip_request_mode: false,
        tax_cert_request_mode: false,
        salary_cert_request_mode: false
    });
    const [savingRequestMode, setSavingRequestMode] = useState({
        slip: false,
        tax: false,
        salary: false
    });
    const [useApproverSignature50Tawi, setUseApproverSignature50Tawi] = useState(false);
    // การยืนยันตัวตนด้วยรูป (หน้าสมัคร/ขอรหัสใหม่) — ดู server/utils/identityVerify.js
    const [identityVerify, setIdentityVerify] = useState({ mode: 'face_card', photoCheck: true });
    const [savingIdentityVerify, setSavingIdentityVerify] = useState('');
    const [savingSignatureSetting, setSavingSignatureSetting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingChat, setSavingChat] = useState(false);
    const [savingSound, setSavingSound] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [editingDb, setEditingDb] = useState(null);

    // Branding settings
    const [branding, setBranding] = useState({ app_name: 'iMoney', app_credit: 'by terza', google_analytics_id: '', ga_cross_domains: '', google_client_id: '' });
    const [savingBranding, setSavingBranding] = useState(false);

    // MOPH Line notify settings
    const [mophNotify, setMophNotify] = useState({
        moph_notify_enabled: false,
        moph_notify_url: 'https://morpromt2f.moph.go.th/api/notify/send',
        moph_notify_client_key: '',
        moph_notify_secret_key: '',
        public_base_url: ''
    });
    const [savingMoph, setSavingMoph] = useState(false);
    const [testingMoph, setTestingMoph] = useState(false);

    // Notify event toggles
    const [notifyEvents, setNotifyEvents] = useState([]);
    const [savingNotifyEvents, setSavingNotifyEvents] = useState(false);
    const [mophTestStatus, setMophTestStatus] = useState(null);

    // Personal Signature and stamp states
    const [mySignatures, setMySignatures] = useState([]);
    const [allSignatures, setAllSignatures] = useState([]);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [stampPreview, setStampPreview] = useState(null);
    const [positionTitle, setPositionTitle] = useState('');
    const [uploadingSignature, setUploadingSignature] = useState(false);
    const [uploadingStamp, setUploadingStamp] = useState(false);
    const [loadingSignatures, setLoadingSignatures] = useState(false);
    const [settingMaster, setSettingMaster] = useState(false);

    // System Reset states
    const [systemStats, setSystemStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetConfirmText, setResetConfirmText] = useState('');
    const [resetting, setResetting] = useState(false);
    const [resetOptions, setResetOptions] = useState({
        users: true,
        sessions: true,
        activity_logs: true,
        signatures: true,
        requests: true,
        chat_messages: true,
        chat_rooms: true,
        user_roles: true
    });

    // Session Timeout states
    const [sessionTimeoutDays, setSessionTimeoutDays] = useState(30);
    const [savingSessionTimeout, setSavingSessionTimeout] = useState(false);

    // Email (Resend) states
    const [emailCfg, setEmailCfg] = useState({ has_api_key: false, api_key_masked: '', resend_from_email: '', resend_from_name: 'iMoney' });
    const [resendApiKeyInput, setResendApiKeyInput] = useState('');
    const [savingEmailCfg, setSavingEmailCfg] = useState(false);
    const [testEmailTo, setTestEmailTo] = useState('');
    const [testingEmail, setTestingEmail] = useState(false);

    // FastReport settings states
    const [reportEngine, setReportEngine] = useState('pdfkit');
    const [fastReportEnabled, setFastReportEnabled] = useState(false);
    const [fastReportServerUrl, setFastReportServerUrl] = useState('http://localhost:4650');
    const [fastReportApiKey, setFastReportApiKey] = useState('');
    const [savingReportEngine, setSavingReportEngine] = useState(false);
    const [testingFastReport, setTestingFastReport] = useState(false);
    const [fastReportStatus, setFastReportStatus] = useState(null);
    const [testing50Tawi, setTesting50Tawi] = useState(false);

    useEffect(() => {
        fetchData();
        fetchMySignatures();
        fetchAllSignatures();
        fetchBranding();
        fetchSessionTimeout();
        fetchReportEngineSettings();
        fetchMophNotify();
        fetchNotifyEvents();
        fetchEmailCfg();
    }, []);

    const fetchEmailCfg = async () => {
        try {
            const res = await api.get('/admin/email-config');
            setEmailCfg(res.data);
        } catch (err) {
            console.error('Error fetching email config:', err);
        }
    };

    const saveEmailCfg = async () => {
        setSavingEmailCfg(true);
        setMessage({ type: '', text: '' });
        try {
            const payload = {
                resend_from_email: emailCfg.resend_from_email,
                resend_from_name: emailCfg.resend_from_name
            };
            // ส่ง API key เฉพาะเมื่อกรอกใหม่ (ไม่ใช่ค่า mask)
            if (resendApiKeyInput.trim()) {
                payload.resend_api_key = resendApiKeyInput.trim();
            }
            await api.put('/admin/email-config', payload);
            setResendApiKeyInput('');
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าอีเมลสำเร็จ' });
            await fetchEmailCfg();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingEmailCfg(false);
        }
    };

    const sendTestEmail = async () => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailTo.trim())) {
            setMessage({ type: 'error', text: 'กรุณากรอกอีเมลปลายทางที่ถูกต้อง' });
            return;
        }
        setTestingEmail(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post('/admin/email-config/test', { to: testEmailTo.trim() });
            setMessage({ type: 'success', text: res.data.message || 'ส่งอีเมลทดสอบสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ส่งอีเมลทดสอบไม่สำเร็จ' });
        } finally {
            setTestingEmail(false);
        }
    };

    const fetchMophNotify = async () => {
        try {
            const res = await api.get('/admin/moph-notify');
            setMophNotify({
                moph_notify_enabled: !!res.data.moph_notify_enabled,
                moph_notify_url: res.data.moph_notify_url || 'https://morpromt2f.moph.go.th/api/notify/send',
                moph_notify_client_key: res.data.moph_notify_client_key || '',
                moph_notify_secret_key: res.data.moph_notify_secret_key || '',
                public_base_url: res.data.public_base_url || ''
            });
        } catch (err) {
            console.error('Error fetching moph notify settings:', err);
        }
    };

    const fetchNotifyEvents = async () => {
        try {
            const res = await api.get('/admin/notify-events');
            setNotifyEvents(res.data.events || []);
        } catch (err) {
            console.error('Error fetching notify events:', err);
        }
    };

    const toggleNotifyEvent = (setting) => {
        setNotifyEvents(prev => prev.map(e => e.setting === setting ? { ...e, enabled: !e.enabled } : e));
    };

    const saveNotifyEvents = async () => {
        setSavingNotifyEvents(true);
        setMessage({ type: '', text: '' });
        try {
            const payload = {};
            notifyEvents.forEach(e => { payload[e.setting] = e.enabled; });
            await api.put('/admin/notify-events', payload);
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าการแจ้งเตือนสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingNotifyEvents(false);
        }
    };

    const saveMophNotify = async () => {
        setSavingMoph(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put('/admin/moph-notify', mophNotify);
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าแจ้งเตือน Line สำเร็จ' });
            fetchMophNotify();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingMoph(false);
        }
    };

    const testMophNotify = async () => {
        setTestingMoph(true);
        setMophTestStatus(null);
        try {
            await api.post('/admin/moph-notify/test', mophNotify);
            setMophTestStatus({ success: true, message: 'ส่งข้อความทดสอบสำเร็จ — ตรวจสอบในกลุ่ม Line' });
        } catch (err) {
            setMophTestStatus({ success: false, message: err.response?.data?.error || 'ส่งข้อความทดสอบไม่สำเร็จ' });
        } finally {
            setTestingMoph(false);
        }
    };

    const fetchBranding = async () => {
        try {
            const res = await api.get('/admin/branding');
            setBranding({
                app_name: res.data.app_name || 'iMoney',
                app_credit: res.data.app_credit || 'by terza',
                google_analytics_id: res.data.google_analytics_id || '',
                ga_cross_domains: res.data.ga_cross_domains || '',
                google_client_id: res.data.google_client_id || ''
            });
        } catch (err) {
            console.error('Error fetching branding:', err);
        }
    };

    const saveBranding = async () => {
        setSavingBranding(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put('/admin/branding', branding);
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าชื่อแอปสำเร็จ - รีเฟรชหน้าเพื่อดูการเปลี่ยนแปลง' });
            // Dispatch event to notify other components
            window.dispatchEvent(new Event('branding-updated'));
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingBranding(false);
        }
    };

    const fetchSessionTimeout = async () => {
        try {
            const res = await api.get('/admin/session-config');
            setSessionTimeoutDays(res.data.session_timeout_days || 30);
        } catch (err) {
            console.error('Error fetching session timeout:', err);
        }
    };

    const saveSessionTimeout = async () => {
        setSavingSessionTimeout(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put('/admin/session-config', { session_timeout_days: sessionTimeoutDays });
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่า Session Timeout สำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingSessionTimeout(false);
        }
    };

    const fetchReportEngineSettings = async () => {
        try {
            const res = await api.get('/admin/report-engine');
            setReportEngine(res.data.report_engine || 'pdfkit');
            setFastReportEnabled(res.data.fastreport_enabled === 'true');
            setFastReportServerUrl(res.data.fastreport_server_url || 'http://localhost:4650');
            setFastReportApiKey(res.data.fastreport_api_key || '');
        } catch (err) {
            console.error('Error fetching report engine settings:', err);
        }
    };

    const saveReportEngine = async () => {
        setSavingReportEngine(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put('/admin/report-engine', {
                report_engine: reportEngine,
                fastreport_enabled: fastReportEnabled,
                fastreport_server_url: fastReportServerUrl,
                fastreport_api_key: fastReportApiKey
            });
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่า Report Engine สำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingReportEngine(false);
        }
    };

    const testFastReportConnection = async () => {
        setTestingFastReport(true);
        setFastReportStatus(null);
        try {
            const res = await api.post('/admin/test-fastreport');
            setFastReportStatus({
                success: res.data.success,
                message: res.data.message
            });
        } catch (err) {
            setFastReportStatus({
                success: false,
                message: err.response?.data?.error || 'ไม่สามารถเชื่อมต่อกับ FastReport Server'
            });
        } finally {
            setTestingFastReport(false);
        }
    };

    const test50TawiPrint = async () => {
        const testCID = '1339900224014';
        const testYear = '2568';

        setTesting50Tawi(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await api.get(`/report/admin/50-tawi/${testCID}/${testYear}`, {
                responseType: 'blob'
            });

            // Create blob and download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `TEST_50TAWI_${testCID}_${testYear}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage({
                type: 'success',
                text: `✅ ทดสอบสำเร็จ! ดาวน์โหลด 50 ทวิ ปี ${testYear} ของ CID ${testCID}`
            });
        } catch (err) {
            setMessage({
                type: 'error',
                text: `❌ เกิดข้อผิดพลาด: ${err.response?.data?.error || err.message}`
            });
        } finally {
            setTesting50Tawi(false);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, dbRes, chatRes, requestModeRes] = await Promise.all([
                api.get('/admin/settings'),
                api.get('/admin/db-config'),
                api.get('/chat/settings').catch(() => ({ data: { chat_enabled: true } })),
                api.get('/admin/request-mode').catch(() => ({ data: {
                    slip_request_mode: false,
                    tax_cert_request_mode: false,
                    salary_cert_request_mode: false
                } }))
            ]);
            setSettings(settingsRes.data);
            setDbConnections(dbRes.data);
            setChatSettings(chatRes.data);
            setRequestModes({
                slip_request_mode: requestModeRes.data.slip_request_mode ?? false,
                tax_cert_request_mode: requestModeRes.data.tax_cert_request_mode ?? false,
                salary_cert_request_mode: requestModeRes.data.salary_cert_request_mode ?? false
            });
            // ดึงค่า use_approver_signature_50tawi จาก settings
            setUseApproverSignature50Tawi(settingsRes.data.use_approver_signature_50tawi === 'true');
            setIdentityVerify({
                mode: settingsRes.data.identity_verify_mode === 'card_only' ? 'card_only' : 'face_card',
                photoCheck: settingsRes.data.identity_photo_check !== 'false'
            });
        } catch (err) {
            console.error('Error fetching settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMySignatures = async () => {
        try {
            setLoadingSignatures(true);
            const res = await api.get('/request/admin/my-signatures');
            setMySignatures(res.data || []);
        } catch (err) {
            console.error('Error fetching signatures:', err);
        } finally {
            setLoadingSignatures(false);
        }
    };

    const fetchAllSignatures = async () => {
        try {
            const res = await api.get('/request/admin/all-signatures');
            setAllSignatures(res.data || []);
        } catch (err) {
            console.error('Error fetching all signatures:', err);
        }
    };

    const setMasterSignature = async (signatureId) => {
        if (!window.confirm('ต้องการตั้งลายเซ็นนี้เป็นลายเซ็นหลักสำหรับใช้ในเอกสารทุกฉบับใช่หรือไม่?')) {
            return;
        }

        try {
            setSettingMaster(true);
            await api.put(`/request/admin/signature/${signatureId}/set-master`);
            setMessage({ type: 'success', text: 'ตั้งเป็นลายเซ็นหลักสำเร็จ' });
            fetchAllSignatures();
            fetchMySignatures();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSettingMaster(false);
        }
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const saveSettings = async () => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put('/admin/settings', settings);
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าสำเร็จ' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    const handleDbChange = (id, field, value) => {
        setDbConnections(prev => prev.map(db =>
            db.id === id ? { ...db, [field]: value } : db
        ));
    };

    const saveDbConnection = async (db) => {
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await api.put(`/admin/db-config/${db.id}`, {
                server: db.server,
                database: db.database_name,
                username: db.username,
                password: db.password
            });
            setMessage({ type: 'success', text: 'บันทึกการตั้งค่าฐานข้อมูลสำเร็จ' });
            setEditingDb(null);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async (db) => {
        setMessage({ type: '', text: '' });
        try {
            await api.post('/admin/db-config/test', {
                server: db.server,
                database: db.database_name,
                username: db.username,
                password: db.password === '********' ? undefined : db.password
            });
            setMessage({ type: 'success', text: `เชื่อมต่อ ${db.name} สำเร็จ` });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เชื่อมต่อไม่สำเร็จ' });
        }
    };

    const toggleChat = async () => {
        setSavingChat(true);
        setMessage({ type: '', text: '' });
        try {
            const newValue = !chatSettings.chat_enabled;
            await api.put('/chat/admin/settings', { chat_enabled: newValue });
            setChatSettings(prev => ({ ...prev, chat_enabled: newValue }));
            setMessage({ type: 'success', text: `${newValue ? 'เปิด' : 'ปิด'}ระบบแชทแล้ว` });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingChat(false);
        }
    };

    const toggleNotificationSound = async () => {
        setSavingSound(true);
        setMessage({ type: '', text: '' });
        try {
            const newValue = !chatSettings.notification_sound;
            await api.put('/chat/admin/settings', { notification_sound: newValue });
            setChatSettings(prev => ({ ...prev, notification_sound: newValue }));
            setMessage({ type: 'success', text: `${newValue ? 'เปิด' : 'ปิด'}เสียงแจ้งเตือนแล้ว` });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingSound(false);
        }
    };

    const toggleRequestMode = async (modeKey, savingKey, modeName) => {
        setSavingRequestMode(prev => ({ ...prev, [savingKey]: true }));
        setMessage({ type: '', text: '' });
        try {
            const newValue = !requestModes[modeKey];
            await api.put('/admin/request-mode', { [modeKey]: newValue });
            setRequestModes(prev => ({ ...prev, [modeKey]: newValue }));
            // หนังสือรับรองเงินเดือนไม่มีโหมด "พิมพ์ได้เอง" — ปิด = ปิดรับคำร้อง/ซ่อนจากผู้ใช้
            const isSalaryCert = modeKey === 'salary_cert_request_mode';
            setMessage({
                type: 'success',
                text: newValue
                    ? `เปิดรับคำร้องสำหรับ${modeName}แล้ว - พนักงานต้องยื่นคำร้อง`
                    : isSalaryCert
                        ? `ปิดรับคำร้องสำหรับ${modeName}แล้ว - ซ่อนตัวเลือกนี้จากผู้ใช้`
                        : `ปิดระบบคำร้องสำหรับ${modeName}แล้ว - พนักงานสามารถพิมพ์ได้เอง`
            });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingRequestMode(prev => ({ ...prev, [savingKey]: false }));
        }
    };

    const testNotificationSound = () => {
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(() => {
                setMessage({ type: 'error', text: 'ไม่พบไฟล์เสียง กรุณาวางไฟล์ notification.mp3 ในโฟลเดอร์ client/public/' });
            });
        } catch (e) {
            setMessage({ type: 'error', text: 'ไม่สามารถเล่นเสียงได้' });
        }
    };

    // Toggle การยืนยันตัวตนด้วยรูป — มีผลทันทีกับหน้าสมัคร/ขอรหัสใหม่ (ทั้ง client และ server อ่านค่าเดียวกัน)
    const toggleIdentityVerify = async (which) => {
        setSavingIdentityVerify(which);
        setMessage({ type: '', text: '' });
        try {
            if (which === 'mode') {
                const mode = identityVerify.mode === 'face_card' ? 'card_only' : 'face_card';
                await api.put('/admin/settings', { identity_verify_mode: mode });
                setIdentityVerify(prev => ({ ...prev, mode }));
                setMessage({
                    type: 'success',
                    text: mode === 'face_card'
                        ? 'เปลี่ยนเป็น "ต้องถ่ายรูปคู่กับบัตร" — ผู้สมัคร/ผู้ขอรหัสใหม่ต้องแนบรูปถือบัตร + รูปบัตร'
                        : 'เปลี่ยนเป็น "ถ่ายเฉพาะบัตร" — แนบรูปบัตรอย่างเดียว (ยืนยันตัวตนอ่อนลง เจ้าหน้าที่ควรตรวจละเอียดขึ้น)'
                });
            } else {
                const on = !identityVerify.photoCheck;
                await api.put('/admin/settings', { identity_photo_check: on ? 'true' : 'false' });
                setIdentityVerify(prev => ({ ...prev, photoCheck: on }));
                setMessage({ type: 'success', text: on ? 'เปิดตรวจรูปอัตโนมัติก่อนแนบแล้ว' : 'ปิดตรวจรูปอัตโนมัติแล้ว — ผู้ใช้แนบรูปใดก็ได้ เจ้าหน้าที่ตรวจเอง' });
            }
            window.dispatchEvent(new Event('branding-updated'));
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingIdentityVerify('');
        }
    };

    // Toggle ลายเซ็น 50 ทวิ (ใช้ของผู้อนุมัติ หรือ ของหัวหน้าการเงิน)
    const toggleApproverSignature50Tawi = async () => {
        setSavingSignatureSetting(true);
        setMessage({ type: '', text: '' });
        try {
            const newValue = !useApproverSignature50Tawi;
            await api.put('/admin/settings', { use_approver_signature_50tawi: newValue ? 'true' : 'false' });
            setUseApproverSignature50Tawi(newValue);
            setMessage({
                type: 'success',
                text: newValue
                    ? 'เปิดใช้ลายเซ็นของผู้อนุมัติในเอกสาร 50 ทวิ'
                    : 'ปิดใช้ลายเซ็นของหัวหน้าการเงิน (ลายเซ็นหลัก) ในเอกสาร 50 ทวิ'
            });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาด' });
        } finally {
            setSavingSignatureSetting(false);
        }
    };

    // Handle signature upload
    const handleSignatureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'error', text: 'กรุณาเลือกไฟล์รูปภาพ' });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setSignaturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadSignature = async () => {
        const fileInput = document.getElementById('signatureInput');
        const file = fileInput?.files[0];
        if (!file) {
            setMessage({ type: 'error', text: 'กรุณาเลือกไฟล์ลายเซ็น' });
            return;
        }

        setUploadingSignature(true);
        setMessage({ type: '', text: '' });

        const formData = new FormData();
        formData.append('signature', file);
        formData.append('signature_type', 'signature');

        try {
            await api.post('/request/admin/signature', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage({ type: 'success', text: 'อัพโหลดลายเซ็นสำเร็จ' });
            setSignaturePreview(null);
            fileInput.value = '';
            fetchMySignatures(); // Refresh the list
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัพโหลด' });
        } finally {
            setUploadingSignature(false);
        }
    };

    // Handle stamp upload
    const handleStampChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'error', text: 'กรุณาเลือกไฟล์รูปภาพ' });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setStampPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadStamp = async () => {
        const fileInput = document.getElementById('stampInput');
        const file = fileInput?.files[0];
        if (!file) {
            setMessage({ type: 'error', text: 'กรุณาเลือกไฟล์ตราประทับ' });
            return;
        }
        if (!positionTitle.trim()) {
            setMessage({ type: 'error', text: 'กรุณาระบุตำแหน่ง' });
            return;
        }

        setUploadingStamp(true);
        setMessage({ type: '', text: '' });

        const formData = new FormData();
        formData.append('signature', file);
        formData.append('signature_type', 'position_stamp');
        formData.append('position_title', positionTitle.trim());

        try {
            await api.post('/request/admin/signature', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage({ type: 'success', text: 'อัพโหลดตราประทับตำแหน่งสำเร็จ' });
            setStampPreview(null);
            setPositionTitle('');
            fileInput.value = '';
            fetchMySignatures(); // Refresh the list
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการอัพโหลด' });
        } finally {
            setUploadingStamp(false);
        }
    };

    const deleteSignature = async (id) => {
        if (!window.confirm('ต้องการลบรายการนี้หรือไม่?')) return;

        try {
            await api.delete(`/request/admin/my-signatures/${id}`);
            setMessage({ type: 'success', text: 'ลบสำเร็จ' });
            fetchMySignatures();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการลบ' });
        }
    };

    // System Reset functions
    const fetchSystemStats = async () => {
        setLoadingStats(true);
        try {
            const res = await api.get('/admin/system-data-stats');
            setSystemStats(res.data);
        } catch (err) {
            console.error('Error fetching system stats:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    const openResetModal = () => {
        fetchSystemStats();
        setShowResetModal(true);
        setResetConfirmText('');
    };

    const closeResetModal = () => {
        setShowResetModal(false);
        setResetConfirmText('');
    };

    const handleResetOptionChange = (key) => {
        setResetOptions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const executeSystemReset = async () => {
        if (resetConfirmText !== 'RESET SYSTEM') {
            setMessage({ type: 'error', text: 'กรุณาพิมพ์ "RESET SYSTEM" เพื่อยืนยัน' });
            return;
        }

        setResetting(true);
        setMessage({ type: '', text: '' });

        try {
            const res = await api.post('/admin/system-reset', {
                confirmText: resetConfirmText,
                options: resetOptions
            });
            setMessage({ type: 'success', text: res.data.message });
            closeResetModal();
            fetchSystemStats();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการรีเซ็ตระบบ' });
        } finally {
            setResetting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-center" style={{ padding: '60px 0' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>ตั้งค่าระบบ</span>
            </div>

            <div className="admin-header">
                <h1>ตั้งค่าระบบ</h1>
                <p>ตั้งค่าทั่วไปและการเชื่อมต่อฐานข้อมูล</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Branding Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ตั้งค่าชื่อแอปพลิเคชัน</div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        เปลี่ยนชื่อแอปและข้อความ Credit ที่แสดงทั่วทั้งเว็บไซต์
                    </p>
                    <div className="branding-settings-grid">
                        <div className="form-group">
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
                                </svg>
                                ชื่อแอปพลิเคชัน
                            </label>
                            <input
                                type="text"
                                className="steam-input"
                                value={branding.app_name}
                                onChange={e => setBranding(prev => ({ ...prev, app_name: e.target.value }))}
                                placeholder="เช่น iMoney"
                            />
                            <span className="form-hint">แสดงที่ Header, Footer และหน้า Login</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                                ข้อความ Credit
                            </label>
                            <input
                                type="text"
                                className="steam-input"
                                value={branding.app_credit}
                                onChange={e => setBranding(prev => ({ ...prev, app_credit: e.target.value }))}
                                placeholder="เช่น by terza"
                            />
                            <span className="form-hint">แสดงใต้ชื่อแอปที่ Header และ Footer</span>
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M19 3H5c-1.11 0-2 .89-2 2v14c0 1.11.89 2 2 2h14c1.11 0 2-.89 2-2V5c0-1.11-.89-2-2-2zM9 17H7v-5h2v5zm4 0h-2V7h2v10zm4 0h-2v-7h2v7z"/>
                                </svg>
                                Google Analytics / Tag Manager ID
                            </label>
                            <input
                                type="text"
                                className="steam-input"
                                value={branding.google_analytics_id}
                                onChange={e => setBranding(prev => ({ ...prev, google_analytics_id: e.target.value.trim() }))}
                                placeholder="เช่น G-XXXXXXXXXX หรือ GTM-XXXXXXX (เว้นว่างเพื่อปิดการติดตาม)"
                            />
                            <span className="form-hint">
                                รหัสสำหรับติดตามการใช้งานเว็บไซต์ — รองรับ GA4 (<code>G-XXXXXXXXXX</code>), GTM (<code>GTM-XXXXXXX</code>) หรือ Universal Analytics (<code>UA-XXXX-X</code>) — เว้นว่างเพื่อไม่ติดตาม
                            </span>
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.93 6h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 0 1 5.08 16zm2.95-8H5.08a7.99 7.99 0 0 1 4.33-3.56A15.65 15.65 0 0 0 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/>
                                </svg>
                                โดเมนสำหรับ Cross-domain (Google Analytics)
                            </label>
                            <input
                                type="text"
                                className="steam-input"
                                value={branding.ga_cross_domains}
                                onChange={e => setBranding(prev => ({ ...prev, ga_cross_domains: e.target.value }))}
                                placeholder="เช่น imoney.sskh.app, imoney.sisakethospital.com"
                            />
                            <span className="form-hint">
                                ใส่โดเมนทั้งหมดที่ใช้กับเว็บนี้ คั่นด้วยจุลภาค (,) เพื่อให้ GA นับทั้งหมดเป็นเว็บเดียวกัน (ป้องกัน session ซ้ำและ self-referral) — เว้นว่างเพื่อใช้ค่าเริ่มต้น
                            </span>
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" width="18" height="18" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Google Client ID (เข้าสู่ระบบ / ผูกอีเมลด้วย Google)
                            </label>
                            <input
                                type="text"
                                className="steam-input"
                                value={branding.google_client_id}
                                onChange={e => setBranding(prev => ({ ...prev, google_client_id: e.target.value.trim() }))}
                                placeholder="เช่น 1234567890-abc.apps.googleusercontent.com (เว้นว่างเพื่อปิด)"
                            />
                            <span className="form-hint">
                                OAuth Client ID (ชนิด Web) จาก Google Cloud Console — ต้องลงท้ายด้วย <code>.apps.googleusercontent.com</code> และตั้ง Authorized JavaScript origins เป็นโดเมนของเว็บนี้ — เว้นว่างเพื่อปิดปุ่ม Google
                            </span>
                        </div>
                    </div>
                    <div className="branding-preview">
                        <span className="preview-label">ตัวอย่าง:</span>
                        <div className="preview-box">
                            <div className="preview-logo">
                                <div className="preview-icon">$</div>
                                <div className="preview-text">
                                    <span className="preview-app-name">{branding.app_name || 'iMoney'}</span>
                                    <span className="preview-credit">{branding.app_credit || 'by terza'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ marginTop: '16px' }}>
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={saveBranding}
                            disabled={savingBranding}
                        >
                            {savingBranding ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Email (Resend) Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    ระบบอีเมล (Resend)
                </div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        ใช้ส่งอีเมลยืนยันและเอกสารให้ผู้ใช้ ผ่านบริการ <a href="https://resend.com" target="_blank" rel="noreferrer">Resend.com</a>
                        {emailCfg.has_api_key
                            ? <span className="badge badge-success" style={{ marginLeft: 8 }}>ตั้งค่าแล้ว</span>
                            : <span className="badge badge-warning" style={{ marginLeft: 8 }}>ยังไม่ได้ตั้งค่า</span>}
                    </p>

                    <div className="form-group">
                        <label className="form-label">Resend API Key</label>
                        <input
                            type="text"
                            className="steam-input"
                            value={resendApiKeyInput}
                            onChange={e => setResendApiKeyInput(e.target.value)}
                            placeholder={emailCfg.has_api_key ? `ตั้งค่าไว้แล้ว (${emailCfg.api_key_masked}) — กรอกใหม่เพื่อเปลี่ยน` : 're_xxxxxxxxxxxxxxxxxxxx'}
                        />
                        <span className="form-hint">
                            สร้างที่ Resend → API Keys — เว้นว่างไว้หากไม่ต้องการเปลี่ยนค่าเดิม
                        </span>
                    </div>

                    <div className="branding-settings-grid">
                        <div className="form-group">
                            <label className="form-label">อีเมลผู้ส่ง (From Email)</label>
                            <input
                                type="email"
                                className="steam-input"
                                value={emailCfg.resend_from_email}
                                onChange={e => setEmailCfg(prev => ({ ...prev, resend_from_email: e.target.value }))}
                                placeholder="noreply@imoney.sisakethospital.com"
                            />
                            <span className="form-hint">ต้องเป็นโดเมนที่ verify แล้วใน Resend</span>
                        </div>
                        <div className="form-group">
                            <label className="form-label">ชื่อผู้ส่ง (From Name)</label>
                            <input
                                type="text"
                                className="steam-input"
                                value={emailCfg.resend_from_name}
                                onChange={e => setEmailCfg(prev => ({ ...prev, resend_from_name: e.target.value }))}
                                placeholder="iMoney"
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: '16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button className="steam-btn steam-btn-primary" onClick={saveEmailCfg} disabled={savingEmailCfg}>
                            {savingEmailCfg ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่าอีเมล'}
                        </button>
                    </div>

                    {/* ทดสอบส่งอีเมล */}
                    <div className="email-test-box">
                        <label className="form-label">ทดสอบส่งอีเมล</label>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <input
                                type="email"
                                className="steam-input"
                                style={{ flex: 1, minWidth: 200 }}
                                value={testEmailTo}
                                onChange={e => setTestEmailTo(e.target.value)}
                                placeholder="อีเมลปลายทางสำหรับทดสอบ"
                            />
                            <button className="steam-btn steam-btn-secondary" onClick={sendTestEmail} disabled={testingEmail}>
                                {testingEmail ? 'กำลังส่ง...' : 'ส่งทดสอบ'}
                            </button>
                        </div>
                    </div>

                    <div className="dns-setup-note">
                        <strong>วิธีตั้งค่าโดเมน imoney.sisakethospital.com ใน Resend:</strong>
                        <ol>
                            <li>ที่ Resend → <em>Domains</em> → <em>Add Domain</em> → กรอก <code>imoney.sisakethospital.com</code></li>
                            <li>Resend จะให้ระเบียน DNS — นำไปเพิ่มที่ผู้ให้บริการโดเมน:
                                <ul>
                                    <li><code>TXT</code> (SPF): <code>send</code> → <code>v=spf1 include:amazonses.com ~all</code></li>
                                    <li><code>TXT</code> (DKIM): <code>resend._domainkey</code> → ค่าที่ Resend ให้</li>
                                    <li><code>MX</code>: <code>send</code> → <code>feedback-smtp.[region].amazonses.com</code> (priority 10)</li>
                                    <li><code>TXT</code> (DMARC, แนะนำ): <code>_dmarc</code> → <code>v=DMARC1; p=none;</code></li>
                                </ul>
                            </li>
                            <li>กลับมาที่ Resend กด <em>Verify DNS Records</em> รอจนขึ้น <em>Verified</em> (ปกติ 5–30 นาที)</li>
                            <li>สร้าง API Key ที่ <em>API Keys</em> แล้วนำมากรอกในช่องด้านบน</li>
                            <li>ตั้ง From Email เป็น <code>noreply@imoney.sisakethospital.com</code> แล้วกดส่งทดสอบ</li>
                        </ol>
                    </div>
                </div>
            </div>

            {/* MOPH Line Notify Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                    </svg>
                    แจ้งเตือนเข้ากลุ่ม Line (MOPH)
                </div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        เมื่อมีคำขอเปิดบัญชีใหม่ ระบบจะส่งแจ้งเตือนเข้ากลุ่ม Line ผ่าน MOPH Notify Gateway
                    </p>

                    <div className="chat-toggle-section mb-3">
                        <div className="toggle-info">
                            <h4>เปิด/ปิดการแจ้งเตือน</h4>
                            <p>{mophNotify.moph_notify_enabled ? 'ระบบจะส่งแจ้งเตือนเข้ากลุ่ม Line' : 'ปิดการแจ้งเตือน Line'}</p>
                        </div>
                        <button
                            className={`toggle-switch ${mophNotify.moph_notify_enabled ? 'active' : ''}`}
                            onClick={() => setMophNotify(prev => ({ ...prev, moph_notify_enabled: !prev.moph_notify_enabled }))}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">{mophNotify.moph_notify_enabled ? 'เปิด' : 'ปิด'}</span>
                        </button>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Endpoint URL</label>
                        <input
                            type="text"
                            className="steam-input"
                            value={mophNotify.moph_notify_url}
                            onChange={e => setMophNotify(prev => ({ ...prev, moph_notify_url: e.target.value }))}
                            placeholder="https://morpromt2f.moph.go.th/api/notify/send"
                        />
                    </div>

                    <div className="form-group" style={{ marginTop: '16px' }}>
                        <label className="form-label">client-key</label>
                        <input
                            type="text"
                            className="steam-input"
                            value={mophNotify.moph_notify_client_key}
                            onChange={e => setMophNotify(prev => ({ ...prev, moph_notify_client_key: e.target.value.trim() }))}
                            placeholder="client-key จาก MOPH"
                            autoComplete="off"
                        />
                    </div>

                    <div className="form-group" style={{ marginTop: '16px' }}>
                        <label className="form-label">secret-key</label>
                        <input
                            type="password"
                            className="steam-input"
                            value={mophNotify.moph_notify_secret_key}
                            onChange={e => setMophNotify(prev => ({ ...prev, moph_notify_secret_key: e.target.value }))}
                            placeholder="secret-key (เว้นว่างไว้หากไม่ต้องการเปลี่ยน)"
                            autoComplete="new-password"
                        />
                        <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                            ระบบจะแสดงเป็น ******** หากตั้งค่าไว้แล้ว — กรอกใหม่เฉพาะเมื่อต้องการเปลี่ยน
                        </span>
                    </div>

                    <div className="form-group" style={{ marginTop: '16px' }}>
                        <label className="form-label">โดเมนสาธารณะ (Public URL)</label>
                        <input
                            type="text"
                            className="steam-input"
                            value={mophNotify.public_base_url}
                            onChange={e => setMophNotify(prev => ({ ...prev, public_base_url: e.target.value.trim() }))}
                            placeholder="https://imoney.example.go.th"
                            autoComplete="off"
                        />
                        <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                            URL สาธารณะของระบบ (HTTPS) — ใช้ให้ LINE ดึงรูปบัตรมาแสดงในกลุ่ม และสร้างปุ่ม "อนุมัติ" ในข้อความ
                            หากเว้นว่าง ระบบจะส่งแจ้งเตือนเป็นข้อความล้วน (ไม่มีรูป/ปุ่ม)
                        </span>
                    </div>

                    {mophTestStatus && (
                        <div className="info-box" style={{
                            backgroundColor: mophTestStatus.success ? '#d1fae5' : '#fee2e2',
                            border: `1px solid ${mophTestStatus.success ? '#6ee7b7' : '#fca5a5'}`,
                            borderRadius: '8px',
                            padding: '12px',
                            marginTop: '16px',
                            fontSize: '14px',
                            color: mophTestStatus.success ? '#047857' : '#b91c1c'
                        }}>
                            {mophTestStatus.message}
                        </div>
                    )}

                    <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={saveMophNotify}
                            disabled={savingMoph}
                        >
                            {savingMoph ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                        </button>
                        <button
                            className="steam-btn steam-btn-secondary"
                            onClick={testMophNotify}
                            disabled={testingMoph}
                        >
                            {testingMoph ? 'กำลังทดสอบ...' : 'ทดสอบส่งข้อความ'}
                        </button>
                    </div>

                    {/* Event toggles — เลือกว่าจะแจ้งเตือนเรื่องอะไรบ้าง */}
                    <div className="notify-events-section">
                        <h4 className="notify-events-title">เลือกเหตุการณ์ที่จะแจ้งเตือนเข้ากลุ่ม</h4>
                        <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
                            เปิด/ปิดแยกแต่ละเรื่อง (ค่าเริ่มต้นปิดทั้งหมด) — ต้องตั้ง client-key/secret-key และกดบันทึกด้านบนก่อน
                        </p>
                        {notifyEvents.length === 0 ? (
                            <p className="text-muted" style={{ fontSize: 13 }}>กำลังโหลด...</p>
                        ) : (
                            <div className="notify-events-list">
                                {notifyEvents.map(ev => (
                                    <div key={ev.setting} className="notify-event-row">
                                        <div className="notify-event-info">
                                            <span className="notify-event-label">{ev.label}</span>
                                            <span className="notify-event-desc">{ev.desc}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className={`toggle-switch ${ev.enabled ? 'active' : ''}`}
                                            onClick={() => toggleNotifyEvent(ev.setting)}
                                        >
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label">{ev.enabled ? 'เปิด' : 'ปิด'}</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ marginTop: 14 }}>
                            <button className="steam-btn steam-btn-primary" onClick={saveNotifyEvents} disabled={savingNotifyEvents}>
                                {savingNotifyEvents ? 'กำลังบันทึก...' : 'บันทึกการแจ้งเตือนรายเหตุการณ์'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Session Timeout Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                    </svg>
                    Session Timeout
                </div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        กำหนดระยะเวลาที่ Session จะหมดอายุอัตโนมัติเมื่อผู้ใช้ไม่ได้ออกจากระบบ
                    </p>

                    <div className="session-timeout-section">
                        <div className="form-group">
                            <label className="form-label">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                                    <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                                </svg>
                                จำนวนวัน
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <input
                                    type="number"
                                    className="steam-input"
                                    style={{ maxWidth: '150px' }}
                                    min="1"
                                    max="365"
                                    value={sessionTimeoutDays}
                                    onChange={(e) => setSessionTimeoutDays(parseInt(e.target.value) || 1)}
                                />
                                <span className="text-muted">วัน</span>
                            </div>
                            <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                                ค่าเริ่มต้น: 30 วัน | ระบุได้ระหว่าง 1-365 วัน
                            </span>
                        </div>

                        <div className="info-box" style={{
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            padding: '12px',
                            marginTop: '16px'
                        }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ color: '#3b82f6', flexShrink: 0 }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                </svg>
                                <div style={{ fontSize: '14px', color: '#1e40af' }}>
                                    <strong>หมายเหตุ:</strong> Session จะหมดอายุอัตโนมัติหากผู้ใช้ไม่มีการใช้งานระบบเกินจำนวนวันที่กำหนด
                                    ผู้ใช้จะต้องเข้าสู่ระบบใหม่อีกครั้ง
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <button
                                className="steam-btn steam-btn-primary"
                                onClick={saveSessionTimeout}
                                disabled={savingSessionTimeout || sessionTimeoutDays < 1 || sessionTimeoutDays > 365}
                            >
                                {savingSessionTimeout ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* FastReport Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                    </svg>
                    Report Engine
                </div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        เลือก Report Engine สำหรับสร้างเอกสาร PDF (สลิปเงินเดือน, หนังสือรับรอง 50 ทวิ)
                    </p>

                    <div className="form-group">
                        <label className="form-label">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                                <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                            </svg>
                            เลือก Engine
                        </label>
                        <select
                            className="steam-input"
                            value={reportEngine}
                            onChange={(e) => setReportEngine(e.target.value)}
                            style={{ maxWidth: '300px' }}
                        >
                            <option value="pdfkit">PDFKit (ค่าเริ่มต้น)</option>
                            <option value="fastreport">FastReport Server</option>
                        </select>
                        <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                            PDFKit: ใช้ library ภายใน | FastReport: ใช้ FastReport Server ภายนอก
                        </span>
                    </div>

                    {reportEngine === 'fastreport' && (
                        <>
                            <div className="form-group" style={{ marginTop: '20px' }}>
                                <label className="form-label">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                    </svg>
                                    เปิดใช้งาน FastReport
                                </label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={fastReportEnabled}
                                        onChange={(e) => setFastReportEnabled(e.target.checked)}
                                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                    />
                                    <span className="text-muted">
                                        {fastReportEnabled ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                                    </span>
                                </div>
                            </div>

                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label className="form-label">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                    </svg>
                                    FastReport Server URL
                                </label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    value={fastReportServerUrl}
                                    onChange={(e) => setFastReportServerUrl(e.target.value)}
                                    placeholder="http://localhost:4650"
                                />
                                <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                                    URL ของ FastReport Server (รวม protocol และ port)
                                </span>
                            </div>

                            <div className="form-group" style={{ marginTop: '16px' }}>
                                <label className="form-label">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                                        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                                    </svg>
                                    API Key
                                </label>
                                <input
                                    type="password"
                                    className="steam-input"
                                    value={fastReportApiKey}
                                    onChange={(e) => setFastReportApiKey(e.target.value)}
                                    placeholder="ใส่ API Key (ถ้ามี)"
                                />
                                <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                                    API Key สำหรับการเชื่อมต่อกับ FastReport Server (ไม่บังคับ)
                                </span>
                            </div>

                            {fastReportStatus && (
                                <div className={`info-box`} style={{
                                    backgroundColor: fastReportStatus.success ? '#d1fae5' : '#fee2e2',
                                    border: `1px solid ${fastReportStatus.success ? '#6ee7b7' : '#fca5a5'}`,
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginTop: '16px'
                                }}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{
                                            color: fastReportStatus.success ? '#059669' : '#dc2626',
                                            flexShrink: 0
                                        }}>
                                            {fastReportStatus.success ? (
                                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                            ) : (
                                                <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                                            )}
                                        </svg>
                                        <div style={{
                                            fontSize: '14px',
                                            color: fastReportStatus.success ? '#047857' : '#b91c1c'
                                        }}>
                                            {fastReportStatus.message}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button
                                    className="steam-btn steam-btn-secondary"
                                    onClick={testFastReportConnection}
                                    disabled={testingFastReport || !fastReportServerUrl}
                                >
                                    {testingFastReport ? 'กำลังทดสอบ...' : 'ทดสอบการเชื่อมต่อ'}
                                </button>
                                <button
                                    className="steam-btn steam-btn-primary"
                                    onClick={test50TawiPrint}
                                    disabled={testing50Tawi}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                        <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                    </svg>
                                    {testing50Tawi ? 'กำลังพิมพ์...' : 'ทดสอบพิมพ์ 50 ทวิ (1339900224014)'}
                                </button>
                            </div>
                        </>
                    )}

                    <div className="info-box" style={{
                        backgroundColor: '#fff7ed',
                        border: '1px solid #fed7aa',
                        borderRadius: '8px',
                        padding: '12px',
                        marginTop: '16px'
                    }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ color: '#ea580c', flexShrink: 0 }}>
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                            </svg>
                            <div style={{ fontSize: '14px', color: '#9a3412' }}>
                                <strong>หมายเหตุ:</strong> ระบบจะ fallback ไปใช้ PDFKit อัตโนมัติหาก FastReport ไม่พร้อมใช้งาน
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '16px' }}>
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={saveReportEngine}
                            disabled={savingReportEngine}
                        >
                            {savingReportEngine ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Chat Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ระบบแชท</div>
                <div className="steam-card-body">
                    <div className="chat-toggle-section">
                        <div className="toggle-info">
                            <h4>เปิด/ปิดระบบแชท</h4>
                            <p>เมื่อปิดระบบ ผู้ใช้จะไม่สามารถเห็นปุ่มแชทได้</p>
                        </div>
                        <button
                            className={`toggle-switch ${chatSettings.chat_enabled ? 'active' : ''}`}
                            onClick={toggleChat}
                            disabled={savingChat}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingChat ? '...' : (chatSettings.chat_enabled ? 'เปิด' : 'ปิด')}
                            </span>
                        </button>
                    </div>

                    <div className="chat-toggle-section" style={{ marginTop: '16px' }}>
                        <div className="toggle-info">
                            <h4>เสียงแจ้งเตือน</h4>
                            <p>เปิด/ปิดเสียงเมื่อมีข้อความใหม่เข้ามา</p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <button
                                className="steam-btn steam-btn-secondary"
                                onClick={testNotificationSound}
                                style={{ padding: '8px 12px', fontSize: '13px' }}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ marginRight: '6px' }}>
                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                </svg>
                                ทดสอบเสียง
                            </button>
                            <button
                                className={`toggle-switch ${chatSettings.notification_sound ? 'active' : ''}`}
                                onClick={toggleNotificationSound}
                                disabled={savingSound}
                            >
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">
                                    {savingSound ? '...' : (chatSettings.notification_sound ? 'เปิด' : 'ปิด')}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="sound-info-box">
                        <div className="sound-info-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                            </svg>
                        </div>
                        <div className="sound-info-content">
                            <h5>วิธีตั้งค่าไฟล์เสียงแจ้งเตือน</h5>
                            <ol>
                                <li>ดาวน์โหลดหรือเตรียมไฟล์เสียง MP3 สำหรับแจ้งเตือน</li>
                                <li>เปลี่ยนชื่อไฟล์เป็น <code>notification.mp3</code></li>
                                <li>วางไฟล์ไว้ที่ <code>client/public/notification.mp3</code></li>
                                <li>กดปุ่ม "ทดสอบเสียง" เพื่อตรวจสอบว่าใช้งานได้</li>
                            </ol>
                            <p className="sound-info-note">
                                แนะนำ: ใช้ไฟล์เสียงสั้นๆ (1-3 วินาที) เพื่อไม่รบกวนการทำงาน
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Request Mode Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ระบบคำร้องขอเอกสาร (แยกตามประเภท)</div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        ตั้งค่าว่าเอกสารแต่ละประเภทต้องยื่นคำร้องก่อนหรือพิมพ์ได้เลย
                    </p>

                    {/* Toggle 1: สลิปเงินเดือน */}
                    <div className="chat-toggle-section">
                        <div className="toggle-info">
                            <h4>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                                </svg>
                                สลิปเงินเดือน
                            </h4>
                            <p>
                                {requestModes.slip_request_mode
                                    ? 'พนักงานต้องยื่นคำร้อง → รออนุมัติ → พิมพ์ได้'
                                    : 'พนักงานสามารถพิมพ์/ดาวน์โหลดได้ทันที'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${requestModes.slip_request_mode ? 'active' : ''}`}
                            onClick={() => toggleRequestMode('slip_request_mode', 'slip', 'สลิปเงินเดือน')}
                            disabled={savingRequestMode.slip}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingRequestMode.slip ? '...' : (requestModes.slip_request_mode ? 'ต้องยื่นคำร้อง' : 'พิมพ์ได้เลย')}
                            </span>
                        </button>
                    </div>

                    {/* Toggle 2: 50 ทวิ */}
                    <div className="chat-toggle-section" style={{ marginTop: '16px' }}>
                        <div className="toggle-info">
                            <h4>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                                </svg>
                                หนังสือรับรองหักภาษี ณ ที่จ่าย (50 ทวิ)
                            </h4>
                            <p>
                                {requestModes.tax_cert_request_mode
                                    ? 'พนักงานต้องยื่นคำร้อง → รออนุมัติ → พิมพ์ได้'
                                    : 'พนักงานสามารถพิมพ์/ดาวน์โหลดได้ทันที'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${requestModes.tax_cert_request_mode ? 'active' : ''}`}
                            onClick={() => toggleRequestMode('tax_cert_request_mode', 'tax', '50 ทวิ')}
                            disabled={savingRequestMode.tax}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingRequestMode.tax ? '...' : (requestModes.tax_cert_request_mode ? 'ต้องยื่นคำร้อง' : 'พิมพ์ได้เลย')}
                            </span>
                        </button>
                    </div>

                    {/* Toggle 3: หนังสือรับรองเงินเดือน */}
                    <div className="chat-toggle-section" style={{ marginTop: '16px' }}>
                        <div className="toggle-info">
                            <h4>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                </svg>
                                หนังสือรับรองเงินเดือน
                            </h4>
                            <p>
                                {requestModes.salary_cert_request_mode
                                    ? 'เปิดรับคำร้อง: พนักงานยื่นคำร้อง → รออนุมัติ → รับที่สำนักงานการเงิน'
                                    : 'ปิดรับคำร้อง: ซ่อนตัวเลือกนี้จากผู้ใช้ (พนักงานยื่นคำร้องไม่ได้)'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${requestModes.salary_cert_request_mode ? 'active' : ''}`}
                            onClick={() => toggleRequestMode('salary_cert_request_mode', 'salary', 'หนังสือรับรองเงินเดือน')}
                            disabled={savingRequestMode.salary}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingRequestMode.salary ? '...' : (requestModes.salary_cert_request_mode ? 'เปิดรับคำร้อง' : 'ปิดรับคำร้อง')}
                            </span>
                        </button>
                    </div>

                    <div className="request-mode-info-box">
                        <div className="request-mode-info-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                            </svg>
                        </div>
                        <div className="request-mode-info-content">
                            <h5>รายละเอียดการทำงาน</h5>
                            <div className="mode-comparison">
                                <div className="mode-box mode-on">
                                    <strong>เมื่อเปิด "ต้องยื่นคำร้อง"</strong>
                                    <ul>
                                        <li>พนักงานต้องยื่นคำร้องก่อน</li>
                                        <li>เจ้าหน้าที่การเงินตรวจสอบและอนุมัติ</li>
                                        <li>หลังอนุมัติจึงพิมพ์หรือดาวน์โหลดได้</li>
                                    </ul>
                                </div>
                                <div className="mode-box mode-off">
                                    <strong>เมื่อปิด "พิมพ์ได้เลย"</strong>
                                    <ul>
                                        <li>พนักงานพิมพ์/ดาวน์โหลดได้ทันที</li>
                                        <li>ระบบบันทึก log ทุกครั้งที่พิมพ์</li>
                                        <li>ไม่ต้องรออนุมัติจากเจ้าหน้าที่</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Identity verification (register / reset password) */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">การยืนยันตัวตนด้วยรูป (สมัครบัญชี / ขอรหัสผ่านใหม่)</div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        กำหนดว่าผู้ใช้ต้องแนบรูปอะไรบ้าง และให้ระบบตรวจรูปอัตโนมัติก่อนแนบหรือไม่ — มีผลทันทีทั้งหน้าเว็บและฝั่งเซิร์ฟเวอร์
                    </p>

                    <div className="chat-toggle-section">
                        <div className="toggle-info">
                            <h4>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                                </svg>
                                รูปที่ต้องแนบ
                            </h4>
                            <p>
                                {identityVerify.mode === 'face_card'
                                    ? 'ต้องถ่ายรูปตนเองถือบัตรประชาชน + รูปบัตร (ปลอดภัยสุด — พิสูจน์ว่าเจ้าของบัตรยื่นเอง)'
                                    : 'ถ่ายเฉพาะบัตรประชาชน (สะดวกกว่า แต่ผู้ที่มีสำเนาบัตรอาจสมัครแทนได้ — เจ้าหน้าที่ควรตรวจเทียบข้อมูลบุคลากรก่อนอนุมัติ)'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${identityVerify.mode === 'face_card' ? 'active' : ''}`}
                            onClick={() => toggleIdentityVerify('mode')}
                            disabled={savingIdentityVerify === 'mode'}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingIdentityVerify === 'mode' ? '...' : (identityVerify.mode === 'face_card' ? 'ถ่ายคู่กับบัตร' : 'เฉพาะบัตร')}
                            </span>
                        </button>
                    </div>

                    <div className="chat-toggle-section" style={{ marginTop: '16px' }}>
                        <div className="toggle-info">
                            <h4>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                </svg>
                                ตรวจรูปอัตโนมัติก่อนแนบ
                            </h4>
                            <p>
                                {identityVerify.photoCheck
                                    ? 'ระบบตรวจในเครื่องผู้ใช้ว่ารูปมีใบหน้า/บัตรตามที่ต้องการ ก่อนให้แนบ — รูปที่ไม่ผ่านแนบไม่ได้ และแก้ภาพกลับด้าน/ตะแคงให้อัตโนมัติ'
                                    : 'ไม่ตรวจ — ผู้ใช้แนบรูปใดก็ได้ เจ้าหน้าที่ตรวจเองตอนอนุมัติ'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${identityVerify.photoCheck ? 'active' : ''}`}
                            onClick={() => toggleIdentityVerify('photoCheck')}
                            disabled={savingIdentityVerify === 'photoCheck'}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingIdentityVerify === 'photoCheck' ? '...' : (identityVerify.photoCheck ? 'เปิด' : 'ปิด')}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Personal Signature & Position Stamp Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ลายเซ็นและตราประทับส่วนตัว</div>
                <div className="steam-card-body">
                    <p className="text-muted mb-3">
                        อัพโหลดลายเซ็นและตราประทับตำแหน่งของคุณ เพื่อใช้ประทับในเอกสารที่คุณอนุมัติ
                    </p>

                    {/* Master Signature Section */}
                    {allSignatures.filter(s => s.signature_type === 'signature').length > 0 && (
                        <div className="master-signature-section mb-3" style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '16px', border: '1px solid #86efac' }}>
                            <h5 style={{ marginBottom: '12px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                                ลายเซ็นหลักสำหรับเอกสาร
                            </h5>
                            <p style={{ fontSize: '13px', color: '#15803d', marginBottom: '12px' }}>
                                ลายเซ็นหลักจะแสดงในสลิปเงินเดือนและหนังสือ 50 ทวิ ทุกฉบับอัตโนมัติ
                            </p>
                            <div className="signatures-list">
                                {allSignatures.filter(s => s.signature_type === 'signature').map(sig => (
                                    <div key={sig.id} className="signature-item" style={{
                                        backgroundColor: sig.is_master ? '#dcfce7' : '#fff',
                                        border: sig.is_master ? '2px solid #22c55e' : '1px solid #e5e7eb'
                                    }}>
                                        <div className="signature-preview-box">
                                            <img src={`/api${sig.file_url}`} alt={sig.signature_type} />
                                        </div>
                                        <div className="signature-details">
                                            {sig.is_master && (
                                                <span style={{
                                                    backgroundColor: '#22c55e',
                                                    color: '#fff',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600'
                                                }}>
                                                    ลายเซ็นหลัก
                                                </span>
                                            )}
                                            <span className="position-text">{sig.owner_name}</span>
                                            <span className="upload-date">
                                                อัพโหลดเมื่อ: {new Date(sig.created_at).toLocaleDateString('th-TH')}
                                            </span>
                                        </div>
                                        {!sig.is_master && (
                                            <button
                                                className="steam-btn steam-btn-sm"
                                                onClick={() => setMasterSignature(sig.id)}
                                                disabled={settingMaster}
                                                style={{ marginLeft: 'auto', backgroundColor: '#22c55e', color: '#fff' }}
                                            >
                                                ตั้งเป็นหลัก
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Toggle: ลายเซ็นสำหรับ 50 ทวิ */}
                    <div className="chat-toggle-section mb-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                        <div className="toggle-info">
                            <h4 style={{ color: '#92400e' }}>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                </svg>
                                ลายเซ็นในเอกสาร 50 ทวิ
                            </h4>
                            <p style={{ color: '#78350f' }}>
                                {useApproverSignature50Tawi
                                    ? 'ใช้ลายเซ็นของผู้อนุมัติแต่ละคน'
                                    : 'ใช้ลายเซ็นหลัก (หัวหน้าการเงิน) เท่านั้น'}
                            </p>
                        </div>
                        <button
                            className={`toggle-switch ${useApproverSignature50Tawi ? 'active' : ''}`}
                            onClick={toggleApproverSignature50Tawi}
                            disabled={savingSignatureSetting}
                        >
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">
                                {savingSignatureSetting ? '...' : (useApproverSignature50Tawi ? 'ผู้อนุมัติ' : 'หัวหน้าการเงิน')}
                            </span>
                        </button>
                    </div>

                    {/* Current Signatures Display */}
                    {loadingSignatures ? (
                        <div className="flex-center" style={{ padding: '20px 0' }}>
                            <div className="spinner spinner-sm"></div>
                        </div>
                    ) : mySignatures.length > 0 && (
                        <div className="current-signatures mb-3">
                            <h5 style={{ marginBottom: '12px', color: '#1f2937' }}>ลายเซ็น/ตราประทับปัจจุบันของคุณ</h5>
                            <div className="signatures-list">
                                {mySignatures.map(sig => (
                                    <div key={sig.id} className="signature-item">
                                        <div className="signature-preview-box">
                                            <img src={`/api${sig.file_url}`} alt={sig.signature_type} />
                                        </div>
                                        <div className="signature-details">
                                            <span className={`sig-type-badge ${sig.signature_type}`}>
                                                {sig.signature_type === 'signature' ? 'ลายเซ็น' : 'ตราประทับตำแหน่ง'}
                                            </span>
                                            {sig.position_title && (
                                                <span className="position-text">{sig.position_title}</span>
                                            )}
                                            <span className="upload-date">
                                                อัพโหลดเมื่อ: {new Date(sig.created_at).toLocaleDateString('th-TH')}
                                            </span>
                                        </div>
                                        <button
                                            className="delete-sig-btn"
                                            onClick={() => deleteSignature(sig.id)}
                                            title="ลบ"
                                        >
                                            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="signature-stamp-grid">
                        {/* Signature Upload */}
                        <div className="upload-section">
                            <h4>ลายเซ็นของคุณ</h4>
                            <p className="text-muted">รูปภาพลายเซ็นของคุณ (PNG แนะนำพื้นหลังโปร่งใส)</p>

                            <div className="upload-preview">
                                {signaturePreview ? (
                                    <img src={signaturePreview} alt="Signature Preview" />
                                ) : (
                                    <div className="upload-placeholder">
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                        </svg>
                                        <span>เลือกไฟล์ลายเซ็น</span>
                                    </div>
                                )}
                            </div>

                            <div className="upload-actions">
                                <input
                                    type="file"
                                    id="signatureInput"
                                    accept="image/*"
                                    onChange={handleSignatureChange}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    className="steam-btn steam-btn-secondary"
                                    onClick={() => document.getElementById('signatureInput').click()}
                                >
                                    เลือกไฟล์
                                </button>
                                {signaturePreview && (
                                    <button
                                        className="steam-btn steam-btn-primary"
                                        onClick={uploadSignature}
                                        disabled={uploadingSignature}
                                    >
                                        {uploadingSignature ? 'กำลังอัพโหลด...' : 'บันทึกลายเซ็น'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Position Stamp Upload */}
                        <div className="upload-section">
                            <h4>ตราประทับตำแหน่ง</h4>
                            <p className="text-muted">รูปภาพตราประทับตำแหน่งของคุณ (PNG แนะนำพื้นหลังโปร่งใส)</p>

                            <div className="form-group" style={{ marginBottom: '12px' }}>
                                <label className="form-label">ตำแหน่งของคุณ <span style={{ color: '#ef4444' }}>*</span></label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    placeholder="เช่น นักวิชาการเงินและบัญชีชำนาญการ"
                                    value={positionTitle}
                                    onChange={(e) => setPositionTitle(e.target.value)}
                                />
                            </div>

                            <div className="upload-preview stamp">
                                {stampPreview ? (
                                    <img src={stampPreview} alt="Stamp Preview" />
                                ) : (
                                    <div className="upload-placeholder">
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                                        </svg>
                                        <span>เลือกไฟล์ตราประทับ</span>
                                    </div>
                                )}
                            </div>

                            <div className="upload-actions">
                                <input
                                    type="file"
                                    id="stampInput"
                                    accept="image/*"
                                    onChange={handleStampChange}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    className="steam-btn steam-btn-secondary"
                                    onClick={() => document.getElementById('stampInput').click()}
                                >
                                    เลือกไฟล์
                                </button>
                                {stampPreview && (
                                    <button
                                        className="steam-btn steam-btn-primary"
                                        onClick={uploadStamp}
                                        disabled={uploadingStamp}
                                    >
                                        {uploadingStamp ? 'กำลังอัพโหลด...' : 'บันทึกตราประทับ'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="signature-info-box">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                        </svg>
                        <div>
                            <strong>หมายเหตุ:</strong> ลายเซ็นและตราประทับของคุณจะถูกประทับลงในเอกสาร PDF ที่คุณอนุมัติโดยอัตโนมัติ
                            <br />แนะนำใช้รูปภาพ PNG ที่มีพื้นหลังโปร่งใสเพื่อผลลัพธ์ที่ดีที่สุด
                        </div>
                    </div>
                </div>
            </div>

            {/* General Settings */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">ตั้งค่าทั่วไป</div>
                <div className="steam-card-body">
                    <div className="settings-grid">
                        <div className="form-group">
                            <label className="form-label">ชื่อโรงพยาบาล</label>
                            <input
                                type="text"
                                className="steam-input"
                                value={settings.hospital_name || ''}
                                onChange={e => handleSettingChange('hospital_name', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">เบอร์โทรศัพท์</label>
                            <input
                                type="text"
                                className="steam-input"
                                value={settings.hospital_phone || ''}
                                onChange={e => handleSettingChange('hospital_phone', e.target.value)}
                            />
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">ที่อยู่</label>
                            <input
                                type="text"
                                className="steam-input"
                                value={settings.hospital_address || ''}
                                onChange={e => handleSettingChange('hospital_address', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">เลขประจำตัวผู้เสียภาษี</label>
                            <input
                                type="text"
                                className="steam-input"
                                value={settings.hospital_tax_id || ''}
                                onChange={e => handleSettingChange('hospital_tax_id', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Session หมดอายุ (ชั่วโมง)</label>
                            <input
                                type="number"
                                className="steam-input"
                                value={settings.session_timeout || 24}
                                onChange={e => handleSettingChange('session_timeout', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">จำนวนครั้ง Login ผิดสูงสุด</label>
                            <input
                                type="number"
                                className="steam-input"
                                value={settings.max_login_attempts || 5}
                                onChange={e => handleSettingChange('max_login_attempts', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">ระยะเวลา Lock บัญชี (นาที)</label>
                            <input
                                type="number"
                                className="steam-input"
                                value={settings.lock_duration || 15}
                                onChange={e => handleSettingChange('lock_duration', e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <button
                            className="steam-btn steam-btn-primary"
                            onClick={saveSettings}
                            disabled={saving}
                        >
                            {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Database Connections */}
            <div className="steam-card">
                <div className="steam-card-header">การเชื่อมต่อฐานข้อมูล</div>
                <div className="steam-card-body">
                    {dbConnections.map(db => (
                        <div key={db.id} className="db-connection-card">
                            <div className="db-header">
                                <div>
                                    <h4>{db.description}</h4>
                                    <span className="text-muted">{db.name}</span>
                                    {db.is_readonly && <span className="badge badge-warning" style={{ marginLeft: '8px' }}>Read Only</span>}
                                </div>
                                <button
                                    className="steam-btn steam-btn-secondary"
                                    onClick={() => setEditingDb(editingDb === db.id ? null : db.id)}
                                >
                                    {editingDb === db.id ? 'ยกเลิก' : 'แก้ไข'}
                                </button>
                            </div>

                            {editingDb === db.id ? (
                                <div className="db-form">
                                    <div className="settings-grid">
                                        <div className="form-group">
                                            <label className="form-label">Server</label>
                                            <input
                                                type="text"
                                                className="steam-input"
                                                value={db.server}
                                                onChange={e => handleDbChange(db.id, 'server', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Database</label>
                                            <input
                                                type="text"
                                                className="steam-input"
                                                value={db.database_name}
                                                onChange={e => handleDbChange(db.id, 'database_name', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Username</label>
                                            <input
                                                type="text"
                                                className="steam-input"
                                                value={db.username}
                                                onChange={e => handleDbChange(db.id, 'username', e.target.value)}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Password</label>
                                            <input
                                                type="password"
                                                className="steam-input"
                                                value={db.password}
                                                onChange={e => handleDbChange(db.id, 'password', e.target.value)}
                                                placeholder="ใส่รหัสผ่านใหม่ หรือเว้นว่างเพื่อใช้รหัสเดิม"
                                            />
                                        </div>
                                    </div>
                                    <div className="db-actions">
                                        <button
                                            className="steam-btn steam-btn-secondary"
                                            onClick={() => testConnection(db)}
                                        >
                                            ทดสอบการเชื่อมต่อ
                                        </button>
                                        <button
                                            className="steam-btn steam-btn-primary"
                                            onClick={() => saveDbConnection(db)}
                                            disabled={saving}
                                        >
                                            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="db-info">
                                    <div className="setting-item">
                                        <span className="setting-label">Server:</span>
                                        <span className="setting-value">{db.server}</span>
                                    </div>
                                    <div className="setting-item">
                                        <span className="setting-label">Database:</span>
                                        <span className="setting-value">{db.database_name}</span>
                                    </div>
                                    <div className="setting-item">
                                        <span className="setting-label">Username:</span>
                                        <span className="setting-value">{db.username}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* System Reset Section */}
            <div className="steam-card mb-3" style={{ borderColor: '#fca5a5' }}>
                <div className="steam-card-header" style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', color: '#991b1b' }}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    รีเซ็ตระบบ (Clear Data)
                </div>
                <div className="steam-card-body">
                    <div className="reset-warning-box">
                        <div className="reset-warning-icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                        </div>
                        <div className="reset-warning-content">
                            <h4>ล้างข้อมูลระบบทั้งหมด</h4>
                            <p>
                                ใช้สำหรับเริ่มต้นระบบใหม่หลังจากสิ้นสุดการทดสอบ
                                ข้อมูลที่ถูกลบจะไม่สามารถกู้คืนได้
                            </p>
                            <ul>
                                <li>ลบผู้ใช้ทั้งหมด (ยกเว้นบัญชี Admin ที่ใช้งานอยู่)</li>
                                <li>ลบ Sessions การเข้าสู่ระบบ</li>
                                <li>ลบประวัติการใช้งาน (Activity Logs)</li>
                                <li>ลบลายเซ็นและตราประทับ</li>
                                <li>ลบคำร้องขอเอกสาร</li>
                                <li>ลบข้อความแชททั้งหมด</li>
                            </ul>
                        </div>
                    </div>
                    <button
                        className="steam-btn reset-system-btn"
                        onClick={openResetModal}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ marginRight: '8px' }}>
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                        เปิดหน้าต่างรีเซ็ตระบบ
                    </button>
                </div>
            </div>

            {/* Reset Modal */}
            {showResetModal && (
                <div className="modal-overlay" onClick={closeResetModal}>
                    <div className="reset-modal" onClick={e => e.stopPropagation()}>
                        <div className="reset-modal-header">
                            <h3>
                                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                </svg>
                                ยืนยันการรีเซ็ตระบบ
                            </h3>
                            <button className="modal-close-btn" onClick={closeResetModal}>×</button>
                        </div>
                        <div className="reset-modal-body">
                            {loadingStats ? (
                                <div className="flex-center" style={{ padding: '20px 0' }}>
                                    <div className="spinner spinner-sm"></div>
                                </div>
                            ) : systemStats && (
                                <div className="reset-stats-grid">
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-users"
                                            checked={resetOptions.users}
                                            onChange={() => handleResetOptionChange('users')}
                                        />
                                        <label htmlFor="reset-users">
                                            <span className="stat-label">ผู้ใช้</span>
                                            <span className="stat-count">{systemStats.users} รายการ</span>
                                        </label>
                                    </div>
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-sessions"
                                            checked={resetOptions.sessions}
                                            onChange={() => handleResetOptionChange('sessions')}
                                        />
                                        <label htmlFor="reset-sessions">
                                            <span className="stat-label">Sessions</span>
                                            <span className="stat-count">{systemStats.sessions} รายการ</span>
                                        </label>
                                    </div>
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-logs"
                                            checked={resetOptions.activity_logs}
                                            onChange={() => handleResetOptionChange('activity_logs')}
                                        />
                                        <label htmlFor="reset-logs">
                                            <span className="stat-label">Activity Logs</span>
                                            <span className="stat-count">{systemStats.activity_logs} รายการ</span>
                                        </label>
                                    </div>
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-signatures"
                                            checked={resetOptions.signatures}
                                            onChange={() => handleResetOptionChange('signatures')}
                                        />
                                        <label htmlFor="reset-signatures">
                                            <span className="stat-label">ลายเซ็น</span>
                                            <span className="stat-count">{systemStats.signatures} รายการ</span>
                                        </label>
                                    </div>
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-requests"
                                            checked={resetOptions.requests}
                                            onChange={() => handleResetOptionChange('requests')}
                                        />
                                        <label htmlFor="reset-requests">
                                            <span className="stat-label">คำร้อง</span>
                                            <span className="stat-count">{systemStats.requests} รายการ</span>
                                        </label>
                                    </div>
                                    <div className="reset-stat-item">
                                        <input
                                            type="checkbox"
                                            id="reset-chat"
                                            checked={resetOptions.chat_messages}
                                            onChange={() => handleResetOptionChange('chat_messages')}
                                        />
                                        <label htmlFor="reset-chat">
                                            <span className="stat-label">ข้อความแชท</span>
                                            <span className="stat-count">{systemStats.chat_messages} รายการ</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            <div className="reset-confirm-section">
                                <p className="confirm-instruction">
                                    พิมพ์ <strong>RESET SYSTEM</strong> เพื่อยืนยันการลบข้อมูล
                                </p>
                                <input
                                    type="text"
                                    className="steam-input reset-confirm-input"
                                    placeholder="พิมพ์ RESET SYSTEM"
                                    value={resetConfirmText}
                                    onChange={(e) => setResetConfirmText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="reset-modal-footer">
                            <button className="steam-btn steam-btn-secondary" onClick={closeResetModal}>
                                ยกเลิก
                            </button>
                            <button
                                className="steam-btn reset-confirm-btn"
                                onClick={executeSystemReset}
                                disabled={resetting || resetConfirmText !== 'RESET SYSTEM'}
                            >
                                {resetting ? 'กำลังรีเซ็ต...' : 'รีเซ็ตระบบ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .chat-toggle-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px;
                    background: #f9fafb;
                    border-radius: 8px;
                }
                .toggle-info h4 {
                    font-size: 16px;
                    font-weight: 600;
                    margin: 0 0 4px 0;
                    color: #1f2937;
                }
                .toggle-info p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }
                .toggle-switch {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px 16px;
                    border: 2px solid #e5e7eb;
                    border-radius: 24px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .toggle-switch.active {
                    border-color: #10b981;
                    background: #ecfdf5;
                }
                .toggle-slider {
                    width: 40px;
                    height: 22px;
                    background: #d1d5db;
                    border-radius: 11px;
                    position: relative;
                    transition: background 0.3s ease;
                }
                .toggle-switch.active .toggle-slider {
                    background: #10b981;
                }
                .toggle-slider::after {
                    content: '';
                    position: absolute;
                    width: 18px;
                    height: 18px;
                    background: white;
                    border-radius: 50%;
                    top: 2px;
                    left: 2px;
                    transition: left 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                }
                .toggle-switch.active .toggle-slider::after {
                    left: 20px;
                }
                .toggle-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #6b7280;
                }
                .toggle-switch.active .toggle-label {
                    color: #10b981;
                }
                .db-connection-card {
                    padding: 20px;
                    border: 1px solid var(--steam-border-light);
                    border-radius: var(--steam-radius-md);
                    margin-bottom: 16px;
                }
                .db-connection-card:last-child {
                    margin-bottom: 0;
                }
                .db-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }
                .db-header h4 {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                .db-info {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                }
                .db-form {
                    margin-top: 16px;
                    padding-top: 16px;
                    border-top: 1px solid var(--steam-border-light);
                }
                .db-actions {
                    display: flex;
                    gap: 12px;
                    margin-top: 16px;
                }
                .sound-info-box {
                    display: flex;
                    gap: 16px;
                    margin-top: 20px;
                    padding: 16px;
                    background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
                    border: 1px solid #bfdbfe;
                    border-radius: 12px;
                }
                .sound-info-icon {
                    flex-shrink: 0;
                    width: 40px;
                    height: 40px;
                    background: #3b82f6;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }
                .sound-info-content h5 {
                    margin: 0 0 12px 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #1e40af;
                }
                .sound-info-content ol {
                    margin: 0 0 12px 0;
                    padding-left: 20px;
                    color: #1e3a8a;
                    font-size: 13px;
                    line-height: 1.8;
                }
                .sound-info-content code {
                    background: #dbeafe;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-family: monospace;
                    font-size: 12px;
                    color: #1e40af;
                }
                .sound-info-note {
                    margin: 0;
                    font-size: 12px;
                    color: #3b82f6;
                    font-style: italic;
                }
                .signature-stamp-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 24px;
                    margin-bottom: 20px;
                }
                .upload-section {
                    background: #f9fafb;
                    border-radius: 12px;
                    padding: 20px;
                }
                .upload-section h4 {
                    margin: 0 0 4px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1f2937;
                }
                .upload-section > p {
                    margin: 0 0 16px 0;
                    font-size: 13px;
                }
                .upload-preview {
                    width: 100%;
                    height: 120px;
                    border: 2px dashed #d1d5db;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: white;
                    margin-bottom: 12px;
                    overflow: hidden;
                }
                .upload-preview img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                .upload-preview.stamp {
                    height: 140px;
                }
                .upload-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    color: #9ca3af;
                }
                .upload-placeholder svg {
                    width: 32px;
                    height: 32px;
                }
                .upload-placeholder span {
                    font-size: 13px;
                }
                .upload-actions {
                    display: flex;
                    gap: 8px;
                }
                .signature-info-box {
                    display: flex;
                    gap: 12px;
                    padding: 14px;
                    background: #fef3c7;
                    border: 1px solid #fcd34d;
                    border-radius: 8px;
                    color: #92400e;
                    font-size: 13px;
                }
                .signature-info-box svg {
                    flex-shrink: 0;
                    margin-top: 2px;
                }
                /* Current signatures display */
                .current-signatures {
                    padding: 16px;
                    background: #f0fdf4;
                    border: 1px solid #86efac;
                    border-radius: 12px;
                }
                .signatures-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                }
                .signature-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: white;
                    border: 1px solid #d1fae5;
                    border-radius: 10px;
                    flex: 1;
                    min-width: 280px;
                    max-width: 400px;
                }
                .signature-preview-box {
                    width: 80px;
                    height: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    overflow: hidden;
                }
                .signature-preview-box img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                .signature-details {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .sig-type-badge {
                    display: inline-block;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                    width: fit-content;
                }
                .sig-type-badge.signature {
                    background: #dbeafe;
                    color: #1e40af;
                }
                .sig-type-badge.position_stamp {
                    background: #fef3c7;
                    color: #92400e;
                }
                .position-text {
                    font-size: 13px;
                    font-weight: 500;
                    color: #374151;
                }
                .upload-date {
                    font-size: 11px;
                    color: #9ca3af;
                }
                .delete-sig-btn {
                    padding: 8px;
                    background: #fee2e2;
                    border: none;
                    border-radius: 8px;
                    color: #dc2626;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .delete-sig-btn:hover {
                    background: #fecaca;
                }
                /* Branding Settings Styles */
                .branding-settings-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .form-hint {
                    display: block;
                    margin-top: 6px;
                    font-size: 12px;
                    color: #9ca3af;
                }
                .branding-preview {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: linear-gradient(135deg, #1a9fff 0%, #0ea5e9 100%);
                    border-radius: 12px;
                }
                .preview-label {
                    font-size: 13px;
                    color: rgba(255,255,255,0.8);
                    font-weight: 500;
                }
                .preview-box {
                    background: rgba(255,255,255,0.15);
                    padding: 12px 20px;
                    border-radius: 8px;
                }
                .preview-logo {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .preview-icon {
                    width: 40px;
                    height: 40px;
                    background: white;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    font-weight: bold;
                    color: #1a9fff;
                }
                .preview-text {
                    display: flex;
                    flex-direction: column;
                }
                .preview-app-name {
                    font-size: 18px;
                    font-weight: 700;
                    color: white;
                    line-height: 1.2;
                }
                .preview-credit {
                    font-size: 11px;
                    color: rgba(255,255,255,0.7);
                    font-weight: 500;
                }
                .request-mode-info-box {
                    display: flex;
                    gap: 16px;
                    margin-top: 20px;
                    padding: 16px;
                    background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
                    border: 1px solid #fde047;
                    border-radius: 12px;
                }
                .request-mode-info-icon {
                    flex-shrink: 0;
                    width: 40px;
                    height: 40px;
                    background: #eab308;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }
                .request-mode-info-content h5 {
                    margin: 0 0 12px 0;
                    font-size: 15px;
                    font-weight: 600;
                    color: #854d0e;
                }
                .mode-comparison {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }
                .mode-box {
                    padding: 12px;
                    border-radius: 8px;
                    font-size: 13px;
                }
                .mode-box strong {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 13px;
                }
                .mode-box ul {
                    margin: 0;
                    padding-left: 18px;
                    line-height: 1.6;
                }
                .mode-box.mode-on {
                    background: #dcfce7;
                    border: 1px solid #86efac;
                    color: #166534;
                }
                .mode-box.mode-off {
                    background: #fee2e2;
                    border: 1px solid #fca5a5;
                    color: #991b1b;
                }
                /* System Reset Styles */
                .reset-warning-box {
                    display: flex;
                    gap: 16px;
                    padding: 20px;
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 1px solid #fca5a5;
                    border-radius: 12px;
                    margin-bottom: 20px;
                }
                .reset-warning-icon {
                    flex-shrink: 0;
                    width: 48px;
                    height: 48px;
                    background: #dc2626;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                }
                .reset-warning-content h4 {
                    margin: 0 0 8px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #991b1b;
                }
                .reset-warning-content p {
                    margin: 0 0 12px 0;
                    font-size: 14px;
                    color: #b91c1c;
                }
                .reset-warning-content ul {
                    margin: 0;
                    padding-left: 20px;
                    font-size: 13px;
                    color: #991b1b;
                    line-height: 1.8;
                }
                .reset-system-btn {
                    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                }
                .reset-system-btn:hover {
                    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
                }
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }
                .reset-modal {
                    background: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 500px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    overflow: hidden;
                }
                .reset-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px 24px;
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border-bottom: 1px solid #fca5a5;
                }
                .reset-modal-header h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #991b1b;
                    display: flex;
                    align-items: center;
                }
                .modal-close-btn {
                    background: none;
                    border: none;
                    font-size: 28px;
                    color: #991b1b;
                    cursor: pointer;
                    padding: 0;
                    line-height: 1;
                }
                .reset-modal-body {
                    padding: 24px;
                }
                .reset-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }
                .reset-stat-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                }
                .reset-stat-item input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    accent-color: #dc2626;
                }
                .reset-stat-item label {
                    flex: 1;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    cursor: pointer;
                }
                .stat-label {
                    font-size: 14px;
                    font-weight: 500;
                    color: #374151;
                }
                .stat-count {
                    font-size: 12px;
                    color: #6b7280;
                    background: #e5e7eb;
                    padding: 2px 8px;
                    border-radius: 10px;
                }
                .reset-confirm-section {
                    padding-top: 16px;
                    border-top: 1px solid #e5e7eb;
                }
                .confirm-instruction {
                    font-size: 14px;
                    color: #374151;
                    margin-bottom: 12px;
                }
                .reset-confirm-input {
                    width: 100%;
                    text-align: center;
                    font-size: 16px;
                    letter-spacing: 1px;
                }
                .reset-modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding: 16px 24px;
                    background: #f9fafb;
                    border-top: 1px solid #e5e7eb;
                }
                .reset-confirm-btn {
                    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
                    color: white;
                    border: none;
                }
                .reset-confirm-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
                }
                .reset-confirm-btn:disabled {
                    background: #d1d5db;
                    cursor: not-allowed;
                }
                @media (max-width: 768px) {
                    .chat-toggle-section {
                        flex-direction: column;
                        gap: 16px;
                        align-items: flex-start;
                    }
                    .sound-info-box {
                        flex-direction: column;
                    }
                    .request-mode-info-box {
                        flex-direction: column;
                    }
                    .mode-comparison {
                        grid-template-columns: 1fr;
                    }
                    .db-info {
                        grid-template-columns: 1fr;
                    }
                    .db-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .signature-stamp-grid {
                        grid-template-columns: 1fr;
                    }
                    .reset-warning-box {
                        flex-direction: column;
                    }
                    .reset-stats-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
}

export default AdminSettings;
